<template>
  <div class="update-lead">
    <b-navbar class="update-lead-navbar d-none d-md-flex">
      <b-navbar-brand href="#">
        <amg-icon icon="AmgIcon" size="40" class="pointer invert" />
      </b-navbar-brand>

      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav class="ml-auto d-flex align-items-center">
          <b-nav-item href="#" class="mr-2">
            <b-form-select
              size="sm"
              v-model="selectedLanguage"
              :options="languageOptions"
              class="language-select"
            ></b-form-select>
          </b-nav-item>

          <b-nav-item href="#" class="mr-2">
            <dark-toggler class="invert" :size="'20'" />
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <b-container v-if="isLoading === true">
      <SecurePaymentFormSkeleton />
    </b-container>

    <template v-if="isLoading === false">
      <b-container v-if="isValidLink && !successResponse">
        <b-card class="update-lead-form">
          <b-row>
            <b-col cols="12">
              {{ t.welcome }}
              <span style="font-weight: bold">
                {{ publickLink.first_name }}
                {{ publickLink.middle_name }} {{ publickLink.last_name }}</span
              >
            </b-col>
          </b-row>
          <b-row>
            <b-col
              :cols="12"
              class="d-flex justify-content-start mt-sm-0 justify-content-sm-center align-items-center"
            >
              <span class="title">
                {{ t.updateInformation }}
              </span>
              <b-img
                class="client-program ml-auto"
                :src="getProgramImageRoute(publickLink.selected_service_id)"
                v-b-tooltip.hover.top
              />
            </b-col>

            <b-col :cols="12" class="border-bottom pb-1 my-1">
              <div
                class="d-flex justify-content-start mt-sm-0 align-items-center"
              >
                <feather-icon
                  class="mr-05 text-gray"
                  icon="LockIcon"
                  size="16"
                />
                <span class="description">
                  {{ t.updateInformationDescription }}
                </span>
              </div>
            </b-col>
          </b-row>

          <validation-observer ref="form" #default="{ invalid }">
            <b-row>
              <b-col cols="12" md="12" sm="12">
                <h5 style="font-weight: bold">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-user-icon lucide-user"
                    style="font-weight: bold"
                  >
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  {{ t.informationPersonal }}
                </h5>
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="12" sm="12" md="4">
                <ValidationProvider
                  name="first_name"
                  :rules="getRules('first_name')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="first-name">
                    <template #label>
                      <div class="custom-label-class">
                        <span>{{ t.first_name }}</span><span class="required-asterisk">*</span>
                      </div>
                    </template>

                    <b-form-input
                      class="input-uppercase"
                      id="first-name"
                      v-model="form.first_name"
                      type="text"
                      required
                      :placeholder="t.placeholder.first_name"
                      :state="getState(errors, form.first_name)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "first_name") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>

              <b-col cols="12" sm="12" md="4">
                <ValidationProvider
                  name="middle_name"
                  :rules="getRules('middle_name')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="middle-name">
                    <template #label>
                      <span class="custom-label-class">{{
                        t.middle_name
                      }}</span>
                    </template>

                    <b-form-input
                      class="input-uppercase"
                      id="first-name"
                      v-model="form.middle_name"
                      type="text"
                      required
                      :placeholder="t.placeholder.middle_name"
                      :state="getState(errors, form.middle_name)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "middle_name") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>

              <b-col cols="12" sm="12" md="4">
                <ValidationProvider
                  name="last_name"
                  :rules="getRules('last_name')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="last-name">
                    <template #label>
                      <div class="custom-label-class">
                        <span>{{ t.last_name }}</span><span class="required-asterisk">*</span>
                      </div>
                    </template>

                    <b-form-input
                      class="input-uppercase"
                      id="last-name"
                      v-model="form.last_name"
                      type="text"
                      required
                      :placeholder="t.placeholder.last_name"
                      :state="getState(errors, form.last_name)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "last_name") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <!-- start identification  -->
            <b-row>
              <b-col cols="12" sm="12" md="4">
                <ValidationProvider
                  name="ssn_number"
                  :rules="getRules('ssn_number')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="ssn-number">
                    <template #label>
                      <span class="custom-label-class">{{
                        t.identification_number.ssn
                      }}</span>
                    </template>

                    <b-form-input
                      class="input-uppercase"
                      id="ssn-number"
                      v-model="form.ssn_number"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      required
                      :placeholder="t.placeholder.identification_number.ssn"
                      :state="getState(errors, form.ssn_number)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                      v-mask="'###-##-####'"
                      @keypress="onlyAllowNumbers($event)"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "ssn_number", 9, 9) }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>

              <b-col cols="12" sm="12" md="4">
                <ValidationProvider
                  name="itin_number"
                  :rules="getRules('itin_number')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="itin-number">
                    <template #label>
                      <span class="custom-label-class">{{
                        t.identification_number.itin
                      }}</span>
                    </template>

                    <b-form-input
                      class="input-uppercase"
                      id="itin-number"
                      v-model="form.itin_number"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      required
                      :placeholder="t.placeholder.identification_number.itin"
                      :state="getState(errors, form.itin_number)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                      v-mask="'###-##-####'"
                      @keypress="onlyAllowNumbers($event)"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "itin_number", 9, 9) }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>

              <b-col cols="12" sm="12" md="4">
                <ValidationProvider
                  name="others_number"
                  :rules="getRules('others_number')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="others-number">
                    <template #label>
                      <span class="custom-label-class">{{
                        t.identification_number.others
                      }}</span>
                    </template>

                    <b-form-input
                      type="text"
                      class="input-uppercase"
                      id="others-number"
                      v-model="form.others_number"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      required
                      :placeholder="t.placeholder.identification_number.others"
                      :state="getState(errors, form.others_number)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                      @keypress="onlyAllowNumbers($event)"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "others_number") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
            </b-row>
            <!-- end identification  -->

            <!-- information contact -->

            <b-row>
              <!-- start dob -->
              <b-col cols="12" sm="12" md="6">
                <b-form-group>
                  <template #label>
                    <div class="custom-label-class">
                      <span>{{ t.dob }}</span><span class="required-asterisk">*</span>
                    </div>
                  </template>
                  <div class="d-flex select-date">
                    <ValidationProvider
                      tag="div"
                      name="selected_month"
                      :rules="getRules('selected_month')"
                      v-slot="{ errors, failedRules }"
                      class="select-validation"
                    >
                      <v-select
                        v-model="selectedMonth"
                        :options="months"
                        :getOptionKey="(option) => option.value"
                        :placeholder="t.Month"
                        :clearable="false"
                        :disabled="isProcessing"
                        @input="updateDays()"
                        style="width: 100%; flex: 1"
                        :searchable="false"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                      >
                        <template #selected-option="opt">
                          {{ getParseMonth(opt.value) }}
                        </template>
                      </v-select>
                      <b-form-invalid-feedback v-if="errors.length > 0">
                        {{ getError(failedRules, "selected_month") }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                    <ValidationProvider
                      tag="div"
                      name="selected_day"
                      :rules="getRules('selected_day')"
                      v-slot="{ errors, failedRules }"
                      class="select-validation"
                    >
                      <v-select
                        v-model="selectedDay"
                        :options="days"
                        :getOptionKey="(option) => option.value"
                        :placeholder="t.Day"
                        :clearable="false"
                        :disabled="isProcessing"
                        style="width: 100%; flex: 1"
                        :searchable="false"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                      ></v-select>
                      <b-form-invalid-feedback v-if="errors.length > 0">
                        {{ getError(failedRules, "selected_day") }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                    <ValidationProvider
                      tag="div"
                      name="selected_day"
                      :rules="getRules('selected_day')"
                      v-slot="{ errors, failedRules }"
                      class="select-validation"
                    >
                      <v-select
                        v-model="selectedYear"
                        :options="years"
                        :getOptionKey="(option) => option.value"
                        :placeholder="t.Year"
                        :clearable="false"
                        :disabled="isProcessing"
                        style="width: 100%; flex: 1"
                        :searchable="false"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                      ></v-select>
                      <b-form-invalid-feedback v-if="errors.length > 0">
                        {{ getError(failedRules, "selected_day") }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </div>
                </b-form-group>
              </b-col>
              <!-- end dob -->

              <!-- start email -->
              <b-col cols="12" sm="12" md="6">
                <ValidationProvider
                  name="email"
                  :rules="getRules('email')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="email">
                    <template #label>
                      <span class="custom-label-class"
                        >{{ t.email }}</span
                      >
                    </template>

                    <b-form-input
                      id="email"
                      v-model="form.email"
                      type="text"
                      required
                      :placeholder="t.placeholder.email"
                      :state="getState(errors, form.email)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "email") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <!-- end email -->

              <!--start address -->
              <b-col cols="12" sm="12">
                <ValidationProvider
                  name="address"
                  :rules="getRules('address')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="address">
                    <template #label>
                      <div class="custom-label-class">
                        <span>{{ t.address }}</span><span class="required-asterisk">*</span>
                      </div>
                    </template>
                    <vue-google-autocomplete
                      class="form-control input-form"
                      id="address"
                      ref="refPrincipalAddress"
                      placeholder="Please, type your address"
                      :value="form.address"
                      v-model="form.address"
                      @placechanged="(address) => setAddress(address, 'principalAddress')"
                      @change="(address) => handleAddress(address, 'principalAddress')"
                      country="us"
                      :disabled="isProcessing"
                    ></vue-google-autocomplete>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "address") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="12" md="12">
                <b-form-group label="APT / Unit / SPC">
                  <template #label>
                    <span class="custom-label-class">{{ t.aptunitspc }}</span>
                  </template>
                  <b-form-input
                    inputmode="numeric"
                    pattern="[0-9]*"
                    v-model="form.principalAddress.apt_unit_spc"
                    :disabled="isProcessing"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <ValidationProvider
                  name="city"
                  :rules="getRules('city')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label="City">
                    <template #label>
                      <div class="custom-label-class">
                        <span>{{ t.city }}</span><span class="required-asterisk">*</span>
                      </div>
                    </template>
                    <b-form-input
                      v-model="form.principalAddress.city"
                      :disabled="isProcessing"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "city") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="12" md="5">
                <ValidationProvider
                  name="state"
                  :rules="getRules('state')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label="State">
                    <template #label>
                      <div class="custom-label-class">
                        <span>{{ t.state }}</span><span class="required-asterisk">*</span>
                      </div>
                    </template>
                    <v-select
                      v-model="form.principalAddress.state"
                      :options="stateOptions"
                      label="state"
                      :reduce="(v) => v.slug"
                      placeholder="Select a state"
                      :disabled="isProcessing"
                    ></v-select>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "state") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>
              </b-col>
              <b-col cols="12" md="3">
                <b-form-group label="Zip Code">
                  <template #label>
                    <span class="custom-label-class">{{ t.zipcode }}</span>
                  </template>
                  <b-form-input
                    inputmode="numeric"
                    pattern="[0-9]*"
                    v-model="form.principalAddress.zip_code"
                    :disabled="isProcessing"
                    @keypress="onlyAllowNumbers($event)"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <!-- end address -->
            </b-row>
            <hr v-if="register_card" />
            <!-- end information contact -->
            <template v-if="register_card">
              <b-row>
                <b-col cols="12" md="12" sm="12">
                  <h5 style="font-weight: bold">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-credit-card-icon lucide-credit-card"
                    >
                      <rect width="20" height="14" x="2" y="5" rx="2" />
                      <line x1="2" x2="22" y1="10" y2="10" />
                    </svg>
                    {{ t.payment_information }}
                  </h5>
                </b-col>
              </b-row>
              <b-row>
                <b-col class="credit-card p-0 px-1" cols="12">
                  <ValidationProvider
                    name="cardholder"
                    :rules="getRules('cardholder')"
                    v-slot="{ errors, failedRules }"
                  >
                    <b-form-group label-for="card-holder">
                      <template #label>
                        <div class="custom-label-class">
                          <span>{{ t.cardholder }}</span><span class="required-asterisk">*</span>
                        </div>
                      </template>

                      <b-form-input
                        class="input-uppercase"
                        id="card-holder"
                        v-model="form.cardholder"
                        type="text"
                        required
                        :placeholder="t.placeholder.cardholder"
                        :state="getState(errors, form.cardholder)"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                        :disabled="isProcessing"
                      ></b-form-input>
                      <b-form-invalid-feedback v-if="errors.length > 0">
                        {{ getError(failedRules, "cardholder") }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </ValidationProvider>

                  <b-form-group label-for="card-number">
                    <template #label>
                      <div class="custom-label-class">
                        <span>{{ t.cardNumber }}</span><span class="required-asterisk">*</span>
                      </div>
                    </template>

                    <ValidationProvider
                      name="cardNumber"
                      :rules="getRules('cardNumber')"
                      v-slot="{ errors, failedRules }"
                    >
                      <div class="position-relative">
                        <!-- Imagen de la tarjeta -->
                        <img
                          v-if="cardImage"
                          :src="require(`@/assets/images/cards/${cardImage}`)"
                          :alt="t.alt.cardImage"
                          class="card-icon-left"
                          :style="errors.length > 0 ? 'top: 34%' : 'top: 50%'"
                        />
                        <feather-icon
                          v-else
                          class="card-icon-left-feather"
                          icon="CreditCardIcon"
                          :style="errors.length > 0 ? 'top: 34%' : 'top: 50%'"
                        />

                        <!-- Input con padding izquierdo ajustado -->
                        <b-form-input
                          id="card-number"
                          v-model="form.cardNumber"
                          type="text"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          required
                          class="card-number-input pl-10"
                          :placeholder="t.placeholder.cardNumber"
                          @input="formatCardNumber"
                          @keypress="onlyAllowNumbers($event)"
                          :state="getState(errors, form.cardNumber)"
                          :class="errors.length > 0 ? 'is-invalid' : ''"
                          maxlength="19"
                          :disabled="isProcessing"
                        ></b-form-input>

                        <b-form-invalid-feedback v-if="errors.length > 0">
                          {{ getError(failedRules, "cardNumber") }}
                        </b-form-invalid-feedback>
                      </div>
                    </ValidationProvider>
                  </b-form-group>

                  <b-form-row>
                    <b-col xs="8">
                      <b-form-group label-for="expiry-date">
                        <template #label>
                          <span class="custom-label-class">
                            <span>{{ t.expiryDate }}</span><span class="required-asterisk">*</span>
                          </span>

                          <b-tooltip
                            target="expiry-date"
                            placement="top"
                            :custom-class="'pl-tooltip'"
                          >
                            <div
                              style="
                                font-size: 0.8rem;
                                display: flex;
                                align-items: center;
                                white-space: nowrap;
                              "
                              class="d-flex justify-content-between align-items-center"
                            >
                              <span
                                style="
                                  text-align: left !important;
                                  flex-grow: 1;
                                "
                                v-html="t.tooltip.expiryDate"
                              ></span>

                              <b-img
                                style="
                                  margin-left: 0.5rem;
                                  width: 80px;
                                  object-fit: contain;
                                "
                                src="~@/assets/images/icons/expiry-date-tooltip.svg"
                                alt="Expiry Date Tooltip"
                                fluid
                              />
                            </div>
                          </b-tooltip>

                          <feather-icon
                            id="expiry-date"
                            class="ml-05 cursor-pointer"
                            icon="HelpCircleIcon"
                            size="15"
                          />
                        </template>

                        <ValidationProvider
                          name="expiryDate"
                          :rules="getRules('expiryDate')"
                          v-slot="{ errors, failedRules }"
                        >
                          <b-form-input
                            id="expiry-date"
                            v-model="formattedExpiryDate"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            type="text"
                            required
                            placeholder="MM/YY"
                            maxlength="5"
                            @input="formatExpiryDate"
                            @keypress="onlyAllowNumbers($event)"
                            :state="getState(errors, formattedExpiryDate)"
                            :class="errors.length > 0 ? 'is-invalid' : ''"
                            :disabled="isProcessing"
                          ></b-form-input>
                          <b-form-invalid-feedback v-if="errors.length > 0">
                            {{ getError(failedRules, "expiryDate") }}
                          </b-form-invalid-feedback>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col xs="4">
                      <b-form-group label-for="cvv">
                        <template #label>
                          <span class="custom-label-class">
                            <span>{{ t.cvv }}</span><span class="required-asterisk">*</span>
                          </span>
                          <b-tooltip
                            target="cvv"
                            placement="top"
                            :custom-class="'pl-tooltip'"
                          >
                            <div
                              style="
                                font-size: 0.8rem;
                                display: flex;
                                align-items: center;
                                white-space: nowrap;
                              "
                              class="d-flex justify-content-between align-items-center"
                            >
                              <span
                                style="
                                  text-align: left !important;
                                  flex-grow: 1;
                                "
                                v-html="t.tooltip.cvv"
                              ></span>

                              <b-img
                                style="
                                  margin-left: 0.5rem;
                                  width: 80px;
                                  object-fit: contain;
                                "
                                src="~@/assets/images/icons/cvv.svg"
                                alt="CVV Tooltip"
                                fluid
                              />
                            </div>
                          </b-tooltip>

                          <feather-icon
                            id="cvv"
                            class="ml-05 cursor-pointer"
                            icon="HelpCircleIcon"
                            size="15"
                          />
                        </template>

                        <ValidationProvider
                          name="cvv"
                          :rules="getRules('cvv')"
                          v-slot="{ errors, failedRules }"
                          tag="div"
                        >
                          <b-form-input
                            id="cvv"
                            v-model="form.cvv"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            type="password"
                            required
                            :placeholder="t.placeholder.cvv"
                            maxlength="4"
                            :state="getState(errors, form.cvv)"
                            :class="errors.length > 0 ? 'is-invalid' : ''"
                            :disabled="isProcessing"
                          ></b-form-input>
                          <b-form-invalid-feedback v-if="errors.length > 0">
                            {{ getError(failedRules, "cvv") }}
                          </b-form-invalid-feedback>
                        </ValidationProvider>
                      </b-form-group>
                    </b-col>

                    <b-col cols="12" sm="12">
                      <ValidationProvider
                        name="request_address"
                        :rules="getRules('request_address')"
                        v-slot="{ errors, failedRules }"
                      >
                        <b-form-group label-for="billing-address">
                          <template #label>
                            <div class="custom-label-class">
                              <span>{{ t.billingAddress }}</span><span class="required-asterisk">*</span>
                            </div>
                          </template>
                          <div class="d-flex">
                            <b-form-radio v-model="form.sameAddress" name="some-radios" value="YES">{{t.yes}}</b-form-radio>
                            <b-form-radio v-model="form.sameAddress" class="ml-2" name="some-radios" value="NO">{{t.no}}</b-form-radio>
                          </div>
                        </b-form-group>
                        <b-form-invalid-feedback v-if="errors.length > 0">
                          {{ getError(failedRules, "request_address") }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-col>

                    <template v-if="form.sameAddress === 'NO'">
                      <!--start address -->
                      <b-col cols="12" sm="12">
                        <ValidationProvider
                          name="billing_address"
                          :rules="getRules('address')"
                          v-slot="{ errors, failedRules }"
                        >
                          <b-form-group label-for="address">
                            <template #label>
                              <div class="custom-label-class">
                                <span>{{ t.address }}</span><span class="required-asterisk">*</span>
                              </div>
                            </template>
                            <vue-google-autocomplete
                              class="form-control input-form"
                              id="address2"
                              ref="refCardAddress"
                              placeholder="Please, type your address"
                              :value="form.Address2"
                              v-model="form.address2"
                              @placechanged="(address) => setAddress(address, 'cardAddress')"
                              @change="(address) => handleAddress(address, 'cardAddress')"
                              country="us"
                              :disabled="isProcessing"
                            ></vue-google-autocomplete>
                            <b-form-invalid-feedback v-if="errors.length > 0">
                              {{ getError(failedRules, "address") }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <b-col cols="12" md="12">
                        <b-form-group label="APT / Unit / SPC">
                          <template #label>
                            <span class="custom-label-class">{{ t.aptunitspc }}</span>
                          </template>
                          <b-form-input
                            inputmode="numeric"
                            pattern="[0-9]*"
                            v-model="form.cardAddress.apt_unit_spc"
                            :disabled="isProcessing"
                          ></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" md="4">
                        <ValidationProvider
                          name="billing_city"
                          :rules="getRules('billing_city')"
                          v-slot="{ errors, failedRules }"
                        >
                          <b-form-group label="City">
                            <template #label>
                              <div class="custom-label-class">
                                <span>{{ t.city }}</span><span class="required-asterisk">*</span>
                              </div>
                            </template>
                            <b-form-input
                              v-model="form.cardAddress.city"
                              :disabled="isProcessing"
                            ></b-form-input>
                            <b-form-invalid-feedback v-if="errors.length > 0">
                              {{ getError(failedRules, "billing_city") }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <b-col cols="12" md="5">
                        <ValidationProvider
                          name="billing_state"
                          :rules="getRules('billing_state')"
                          v-slot="{ errors, failedRules }"
                        >
                          <b-form-group label="State">
                            <template #label>
                              <div class="custom-label-class">
                                <span>{{ t.state }}</span><span class="required-asterisk">*</span>
                              </div>
                            </template>
                            <v-select
                              v-model="form.cardAddress.state"
                              :options="stateOptions"
                              label="state"
                              :reduce="(v) => v.slug"
                              placeholder="Select a state"
                              :disabled="isProcessing"
                            ></v-select>
                            <b-form-invalid-feedback v-if="errors.length > 0">
                              {{ getError(failedRules, "billing_state") }}
                            </b-form-invalid-feedback>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>
                      <b-col cols="12" md="3">
                        <b-form-group label="Zip Code">
                          <template #label>
                            <span class="custom-label-class">{{ t.zipcode }}</span>
                          </template>
                          <b-form-input
                            inputmode="numeric"
                            pattern="[0-9]*"
                            v-model="form.cardAddress.zip_code"
                            :disabled="isProcessing"
                            @keypress="onlyAllowNumbers($event)"
                          ></b-form-input>
                        </b-form-group>
                      </b-col>
                      <!-- end address -->
                    </template>

                  </b-form-row>
                </b-col>
              </b-row>
            </template>
            <b-row>
              <b-col cols="12">
                <div class="custom-label-class d-flex justify-content-end">
                  <span class="required-asterisk">(*)</span><span class="required-text">{{ t.requiredFields }}</span>
                </div>
              </b-col>
              <b-col cols="12">
                <b-button
                  type="button"
                  variant="outline-success"
                  block
                  @click="handleOnSubmit"
                  :disabled="isProcessing || invalid"
                >
                  <b-spinner
                    v-if="isProcessing"
                    variant="primary"
                    label="Spinning"
                  ></b-spinner>

                  <span v-if="!isProcessing">
                    {{ t.button.save }}
                  </span>
                </b-button>
              </b-col>
            </b-row>
          </validation-observer>
        </b-card>
      </b-container>

      <template v-if="!isValidLink && !successResponse">
        <b-container>
          <div class="expired-link-container">
            <div class="expired-card text-center">
              <div class="icon-container">
                <img
                  class="icon"
                  :src="
                    require(`@/assets/images/icons/update-lead/user_icon.png`)
                  "
                  alt="Invalid Link Icon"
                />
              </div>

              <h2 class="title">{{ t.invalidLink }}</h2>

              <p class="description">
                {{ t.invalidLinkDescription }}
              </p>
            </div>
          </div>
        </b-container>
      </template>

      <template v-if="successResponse">
        <b-container>
          <div class="success-link-container">
            <div class="success-card text-center">
              <div class="icon-container">
                <img
                  class="icon"
                  :src="
                    require(`@/assets/images/icons/update-lead/user_icon.png`)
                  "
                  alt="Valid Link Icon"
                />
              </div>

              <h2 class="title">{{ t.validLink }}</h2>

              <p class="description">
                {{ successText }}
              </p>
            </div>
          </div>
        </b-container>
      </template>
    </template>
  </div>
</template>

<script>
import DarkToggler from "@core/layouts/components/app-navbar/components/DarkToggler.vue";
import translations from "@/views/pages/lead-update-form/data/translations.js";
import { encryptTextWithRsa, encryptData } from "@/utils/encryption.js";
import UpdateLeadService from "@/views/pages/lead-update-form/service/update.lead.service.js";
import { updateLeadRules } from "@/views/pages/lead-update-form/data/update.lead.rules.js";
import SecurePaymentInvalid from "@/views/pages/secure-payment-form/SecurePaymentInvalid.vue";
import SecurePaymentValid from "@/views/pages/secure-payment-form/SecurePaymentValid.vue";
import SecurePaymentFormSkeleton from "@/views/pages/secure-payment-form/SecurePaymentFormSkeleton.vue";
import VueGoogleAutocomplete from "vue-google-autocomplete";
export default {
  components: {
    DarkToggler,
    SecurePaymentInvalid,
    SecurePaymentValid,
    SecurePaymentFormSkeleton,
    VueGoogleAutocomplete,
  },
  data() {
    return {
      messages: [],
      info: {
        // concept: null,
        // name: null,
        // amount: null,
        // program_id: null,
        // type: null,
        currency: "USD", // set default value
        // language: null,
        lead_id: null, // nullable
        // first_program_id: null,
        // payment_link_details: null,
        // payment_option: null,
        // link_type: null,
      },
      form: {
        first_name: null,
        last_name: null,
        middle_name: null,
        identification_number: null,
        dob: null,
        email: null,
        cardNumber: null,
        expiryMonth: null,
        expiryYear: null,
        cvv: null,
        cardholder: null,
        identification_type: "ssn",
        address: null,
        address2: null,
        sameAddress: null,
        cardAddress: {
          address: null,
          city: null,
          state: null,
          zip_code: null,
          country: null,
          apt_unit_spc: null,
        },
        principalAddress: {
          address: null,
          city: null,
          state: null,
          zip_code: null,
          country: null,
          apt_unit_spc: null,
        },
        stateSelected: null,
        ssn_number: null,
        itin_number: null,
        others_number: null,
      },
      cardImage: null,
      languageOptions: [
        { value: "es", text: "Español" },
        { value: "en", text: "English" },
      ],
      selectedLanguage: "es",
      updateLeadRules, // reglas por campo
      formattedExpiryDate: "", // Campo que mostrará MM/YY
      // isLoading: null,
      isLoading: false,
      // isValidLink: false,
      isValidLink: false,
      successResponse: false, // Indica si se ha completado la operación con éxito
      isProcessing: false,
      stateOptions: [],
      configDob: {
        altFormat: "m/d/Y", // Mostrar en formato DD/MM/YYYY
        dateFormat: "Y-m-d", // Enviar en formato YYYY-MM-DD
        altInput: true,
        maxDate: new Date().getFullYear() - 18 + "-12-31",
      },
      publickLink: null,

      selectedDay: null,
      selectedMonth: null,
      selectedYear: null,
      years: this.generateYears(),
      days: this.updateDays(),
      register_card: false,
      copyPrincipalAddress: null,
      copyCardAddress: null,
    };
  },
  computed: {
    t() {
      return translations[this.selectedLanguage];
    },
    uniqueCode() {
      return this.$route.params.unique_code;
    },
    identificationTypeOptions() {
      return this.t.identification_type_options;
    },
    isOthers() {
      this.form.identification_number = null;
      return this.form.identification_type === "others";
    },
    attrIdentificationNumber() {
      const type = this.form.identification_type;
      return {
        placeholder: this.t.placeholder.identification_number[type],
        label: this.t.identification_number[type],
      };
    },
    successText() {
      const middle_name = this.form.middle_name
        ? `${this.form.middle_name}`
        : "";
      const text = `${this.form.first_name} ${middle_name} ${this.form.last_name}${this.t.validLinkDescription}`;
      return text.replace(/ +/g, " ");
    },
    months() {
      if (!this.t) {
        console.log("Translations not loaded");
      }
      const months = [
        { value: 1, label: `${this.t.Enero}` },
        { value: 2, label: `${this.t.Febrero}` },
        { value: 3, label: `${this.t.Marzo}` },
        { value: 4, label: `${this.t.Abril}` },
        { value: 5, label: `${this.t.Mayo}` },
        { value: 6, label: `${this.t.Junio}` },
        { value: 7, label: `${this.t.Julio}` },
        { value: 8, label: `${this.t.Agosto}` },
        { value: 9, label: `${this.t.Septiembre}` },
        { value: 10, label: `${this.t.Octubre}` },
        { value: 11, label: `${this.t.Noviembre}` },
        { value: 12, label: `${this.t.Diciembre}` },
      ];
      return months;
    },
  },
  methods: {
    getRules(field) {
      return this.updateLeadRules[field];
    },
    getState(errorsList, field) {
      return errorsList.length > 0 ? false : field ? true : null;
    },
    getError(error, validationProperty, max = 0, min = 0) {
      let property = Object.keys(error)[0];
      max = max > 0 ? max : this.updateLeadRules[validationProperty].max;
      min = min > 0 ? min : this.updateLeadRules[validationProperty].min;
      let charactersLong = this.t.validation.charactersLong;
      let characters = this.t.validation.characters;
      if (["ssn_number", "itin_number"].includes(validationProperty)) {
        charactersLong = this.t.validation.digits;
        characters = this.t.validation.digits;
      }
      const messages = {
        required: `${this.t.validation.required}`,
        "only-letters-all": `${this.t.validation.onlyLetters}`,
        min: `${this.t.validation.mustBeAtLeast} ${min} ${charactersLong}`,
        max: `${this.t.validation.mustNotExceed} ${max} ${characters}.`,
        is_numeric: `${this.t.validation.mustBeNumeric}`,
        "valid-expiration-date": `${this.t.validation.invalidExpirationDate}`,
        email: `${this.t.validation.invalidEmail}`,
      };

      return messages[property];
    },
    onlyAllowNumbers(event) {
      const key = event.key;
      // Permite sólo números y las teclas de control como backspace
      if (!/[0-9]/.test(key) && key !== "Backspace" && key !== "Delete") {
        event.preventDefault(); // Previene que el carácter no numérico sea ingresado
      }
    },
    formatCardNumber() {
      // Elimina todos los caracteres no numéricos
      let formatted = this.form.cardNumber.replace(/\D/g, "");

      // Inserta los separadores "-" cada 4 dígitos
      formatted = formatted.replace(/(.{4})(?=.)/g, "$1-");

      // Asigna el valor formateado de vuelta al modelo
      this.form.cardNumber = formatted;

      this.onCardNumberInput();
    },
    formatExpiryDate(event) {
      // Obtén el valor formateado desde el input y elimina cualquier carácter no numérico
      let value = this.formattedExpiryDate.replace(/[^0-9]/g, ""); // Elimina caracteres no numéricos

      // Si la longitud del valor es mayor a 2, agregar el separador "/"
      if (value.length > 2) {
        value = value.slice(0, 2) + "/" + value.slice(2, 4); // Formatea a MM/YY
      }

      // Asigna el valor formateado a `formattedExpiryDate`
      this.formattedExpiryDate = value;

      // Sincronizar mes y año con `payment`
      const [month, year] = value.split("/");
      this.form.expiryMonth = month || "";
      this.form.expiryYear = year || "";
    },
    onCardNumberInput() {
      // Elimina cualquier carácter no numérico (incluye espacios, guiones, etc.)
      const cardNumber = this.form.cardNumber.replace(/\D/g, "");

      // Tipos de tarjetas con sus prefijos (inclusión de más tarjetas como Diners Club, etc.)
      const cardTypes = {
        visa: { prefix: ["4"], image: "visa2.png" },
        mastercard: {
          prefix: [
            "5",
            "222",
            "23",
            "24",
            "25",
            "26",
            "27",
            "28",
            "29",
            "30",
            "31",
            "32",
            "33",
          ],
          image: "mastercard2.png",
        },
        amex: { prefix: ["34", "37"], image: "american.png" },
        discover: {
          prefix: ["6011", "622", "64", "65"],
          image: "Discover.jpg",
        },
        jcb: { prefix: ["35"], image: "jcb2.png" },
        diners: { prefix: ["30", "36", "38", "39"], image: "diners.png" },
      };

      // Itera sobre cada tipo de tarjeta y verifica si el número coincide con algún prefijo
      for (const type in cardTypes) {
        if (
          cardTypes[type].prefix.some((prefix) => cardNumber.startsWith(prefix))
        ) {
          this.cardImage = cardTypes[type].image;
          return;
        }
      }

      // Si no se encuentra ninguna coincidencia, limpia la imagen de la tarjeta
      this.cardImage = null;
    },
    formatCurrency(amount, symbolPosition = "right") {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: this.info.currency,
      });

      const formattedAmount = formatter.format(amount);

      // Detectar la posición del símbolo de moneda en el valor formateado
      const isSymbolFirst = formattedAmount.startsWith(
        formatter.format(0).replace(/\d/g, "")
      );

      if (symbolPosition === "left" && !isSymbolFirst) {
        // Mover el símbolo a la izquierda si no está al principio
        const [value, symbol] = formattedAmount.match(/([^0-9.,]+)|([\d.,]+)/g);
        return `${symbol} ${value}`;
      }

      if (symbolPosition === "right" && isSymbolFirst) {
        // Mover el símbolo a la derecha si está al principio
        const [symbol, value] = formattedAmount.match(/([^0-9.,]+)|([\d.,]+)/g);
        return `${value} ${symbol}`;
      }

      // Devolver el valor formateado directamente si ya está en la posición correcta
      return formattedAmount;
    },
    async handleOnSubmit() {
      try {
        const validate = await this.$refs.form.validate();
        if (!validate) return;

        this.isProcessing = true;
        console.log("same address", this.form.sameAddress);
        this.form.dob = `${this.selectedYear.value}-${this.selectedMonth.value}-${this.selectedDay.value}`;
        let sensitiveInfo = {
          lead_id: this.info.lead_id,
          cardholder: this.form.cardholder?.toUpperCase(),
          cardNumber: this.form.cardNumber,
          expiryMonth: this.form.expiryMonth,
          expiryYear: this.form.expiryYear,
          cvv: this.form.cvv,
          unique_code: this.uniqueCode,
          first_name: this.form.first_name.toUpperCase(),
          last_name: this.form.last_name.toUpperCase(),
          identification_number: this.form.identification_number,
          dob: this.form.dob,
          email: this.form.email,
          identification_type: this.form.identification_type,
          address: this.form.address,
          type: "REGISTER CARD",
          principalAddress: this.form.principalAddress,
          cardAddress: this.form.sameAddress == 'YES'? this.form.principalAddress : this.form.cardAddress,
          ssn_number: this.form.ssn_number,
          itin_number: this.form.itin_number,
          others_number: this.form.others_number,
          middle_name: this.form.middle_name,
          register_card: this.register_card,
        };
        // Convertir objeto a JSON y luego a ArrayBuffer
        const encoder = new TextEncoder();
        const plainData = encoder.encode(JSON.stringify(sensitiveInfo));
        const encryptedData = await encryptData(plainData);

        let payload = {
          encryptedPayload: encryptedData.encryptedData, // Datos cifrados (sensible)
          iv: encryptedData.iv, // IV en Base64 (no es sensible)
          tag: encryptedData.tag, // Tag en Base64 (no es sensible)
          key: encryptTextWithRsa(encryptedData.key), // Clave de encriptación cifrada con RSA (sensible si no se cifra)
        };
        const { data } = await UpdateLeadService.register(payload);
        this.successResponse = true;
      } catch (error) {
        console.log({ error });
        const response = error?.response;
        if (response?.data) {
          const message = response.data[this.selectedLanguage];
          const title = response.data.title[this.selectedLanguage];
          this.showErrorSwal(message, title);
        }
      } finally {
        this.isProcessing = false;
      }
    },
    async checkIfIsValidLink() {
      try {
        this.isLoading = true;

        const { data } = await UpdateLeadService.isValidLink(this.uniqueCode);
        this.isValidLink = data.valid;
        // this.isValidLink = true;

        if (!this.isValidLink) {
          this.isLoading = false;
        }
      } catch (error) {
        this.showErrorSwal(error, this.t.errorMessages.error);
      }
    },
    async getLinkData() {
      try {
        const { data } = await UpdateLeadService.getLinkData(this.uniqueCode);
        this.publickLink = data;
        this.form.first_name = data?.first_name;
        this.form.middle_name = data?.middle_name;
        this.form.last_name = data?.last_name;
        this.register_card = !!data?.show_card;
        this.selectedLanguage = data?.language;
        this.changeFavicon(
          this.getProgramImageRoute(data?.selected_service_id)
        );
      } catch (error) {
        this.showErrorSwal(error, this.t.errorMessages.error);
      } finally {
        this.isLoading = false;
      }
    },
    setAddress(address, type) {
      const location = address;
      const auxAddress = `${
        address.street_number ? address.street_number : ""
      } ${location.route}`;
      const mainAddress = address.street_number
        ? `${address.street_number} ${address.route}`
        : address.route;

      this.form[type].address = mainAddress || " ";
      this.form[type].city = address.locality || " ";
      this.form[type].state =
        address.administrative_area_level_1 || " ";
      this.form[type].zip_code = address.postal_code || " ";
      this.form[type].country = address.country || " ";
      if(type === "principalAddress") {
        this.$refs[`refPrincipalAddress`].$el.value = auxAddress;
        this.copyPrincipalAddress = auxAddress;
      } else {
        this.$refs[`refCardAddress`].$el.value = auxAddress;
        this.copyCardAddress = auxAddress;
      }

      if(this.form[type].apt_unit_spc) {
        const refType = type === "principalAddress" ? "refPrincipalAddress" : "refCardAddress";
        this.$refs[refType].$el.value = `${auxAddress} ${this.form[type].apt_unit_spc}`;
        this.form[type].address = `${auxAddress} ${this.form[type].apt_unit_spc}`;
      }
    },
    handleAddress(address, type) {
      const formType = type === "principalAddress" ? "address" : "address2";
      this.form[formType] = address;
    },
    async getStates() {
      try {
        const data = await UpdateLeadService.getStates();
        this.stateOptions = data;
      } catch (e) {
        console.log(e);
      }
    },
    changeFavicon(iconUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = iconUrl;
    },
    generateYears() {
      const currentYear = new Date().getFullYear();
      const startYear = 1900;
      const minYear = currentYear - 17; // Año límite para mayores de 17 años
      let years = [];

      for (let year = minYear; year >= startYear; year--) {
        years.push({ value: year, label: year.toString() });
      }

      return years;
    },

    updateDays() {
      // Si no hay mes seleccionado, generar hasta 31 días por defecto
      const daysInMonth = !this.selectedMonth
        ? 31
        : this.selectedMonth.value === 2
        ? 29
        : 31;

      this.days = Array.from({ length: daysInMonth }, (_, i) => ({
        value: i + 1,
        label: (i + 1).toString().padStart(2, "0"), // Convierte el día a dos dígitos
      }));
      return this.days;
    },
    formatMonthLabel(month) {
      if (!month) return "";
      return this.selectedMonth && this.selectedMonth.value === month.value
        ? month.value.toString().padStart(2, "0") // Muestra el número del mes con dos dígitos
        : month.label; // Muestra el nombre completo en las opciones
    },
    getParseMonth(value) {
      return Number(value) > 9 ? value : `0${value}`;
    },
  },
  async created() {
    await this.checkIfIsValidLink();
    await this.getStates();
  },
  watch: {
    isValidLink(newValue) {
      if (newValue !== null && newValue === true) {
        this.getLinkData();
      }
    },
    isDarkSkin(value) {
      this.$setFlatpickrTheme(value);
    },
    "t.updateInformation"(value) {
      document.title = value;
    },
    "form.principalAddress.apt_unit_spc"(value) {
      if(!this.$refs[`refPrincipalAddress`].$el.value) return;
      this.$refs[`refPrincipalAddress`].$el.value = `${this.copyPrincipalAddress} ${value}`;
      this.form.principalAddress.address = `${this.copyPrincipalAddress} ${value}`;
    },
    "form.cardAddress.apt_unit_spc"(value) {
      if(!this.$refs[`refCardAddress`].$el.value) return;
      this.$refs[`refCardAddress`].$el.value = `${this.copyCardAddress} ${value}`;
      this.form.cardAddress.address = `${this.copyCardAddress} ${value}`;
    }
  },
  mounted() {
    this.$setFlatpickrTheme(this.isDarkSkin);
    document.title = this.t.updateInformation;
  },
};
</script>
<style lang="scss">
.pl-tooltip .tooltip-inner {
  max-width: fit-content !important;
  width: fit-content !important;
}
</style>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");
.select-validation {
  flex-grow: 1;
  &:nth-child(1),
  &:nth-child(2) {
    max-width: 31%;
    flex-basis: 31%;
  }
  &:last-child {
    max-width: 38%;
    flex-basis: 38%;
  }
}
.update-lead {
  font-family: "Montserrat", sans-serif !important;

  &-navbar {
    background-color: #fff;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  }

  .input-uppercase {
    text-transform: uppercase;

    &::placeholder {
      text-transform: none;
    }
  }

  .custom-label-class {
    font-size: clamp(0.75rem, 1.3vw, 1rem);
  }

  .mr-05 {
    margin-right: 0.5rem;
  }

  .ml-05 {
    margin-left: 0.5rem;
  }

  &-form {
    max-width: 95vw; // Tamaño por defecto (para pantallas muy pequeñas)
    margin: 0 auto;
    margin-top: 3rem;
    padding: 1.25rem;

    // Móviles pequeños (hasta 575px)
    @media (max-width: 575.98px) {
      max-width: 95vw;
    }

    // Móviles grandes y tablets pequeñas (576px - 767px)
    @media (min-width: 576px) and (max-width: 767.98px) {
      max-width: 80vw;
    }

    // Tablets y pantallas medianas (768px - 1023px)
    @media (min-width: 768px) and (max-width: 1023.98px) {
      max-width: 70vw;
    }

    // Laptops y pantallas grandes (1024px - 1399px)
    @media (min-width: 1024px) and (max-width: 1399.98px) {
      max-width: 50vw;
    }

    // Pantallas extra grandes (1400px en adelante)
    @media (min-width: 1400px) {
      max-width: 40vw;
    }
  }

  .language-select {
    width: auto;
    height: 35px;
    font-size: 1rem;
  }

  .update-lead-navbar {
    .b-navbar-nav,
    .b-nav-item,
    .b-navbar-brand,
    .b-navbar-toggle {
      display: flex;
      align-items: center;
    }
  }

  // 1rem → Tamaño mínimo de la fuente (para pantallas pequeñas).
  // 3vw → Tamaño flexible basado en el ancho de la pantalla.
  // 1.8rem → Tamaño máximo de la fuente (para pantallas grandes).

  .title {
    color: #323232;
    font-size: clamp(1.3rem, 2.3vw, 1.8rem);
    font-weight: 700 !important;
  }

  .description {
    font-size: 0.9rem;
    font-weight: 500;
    color: #6e6e6e;
  }
  .required-asterisk {
    color: #dc3545;
    margin-left: 4px;
    font-weight: bold;
  }
  .required-text {
    color: #dc3545;
    margin-left: 4px;
  }
  .credit-card {
    .position-relative {
      position: relative;
    }

    .card-icon-left {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 30px; // Ajusta según el tamaño deseado
      height: auto;
    }

    .card-icon-left-feather {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 1.5rem;
      height: auto;
    }

    .card-number-input {
      padding-left: 50px; // Espacio para la imagen
    }
  }
}

.dark-layout {
  .update-lead {
    &-navbar {
      background-color: #17171a !important;
    }

    .invert {
      filter: invert(100%);
    }

    .title {
      color: #ffffff;
    }

    .description {
      color: #6e6e6e;
    }
  }
}
.type-identification-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.expired-link-container {
  max-width: 90vw;
  margin: 0 auto;
  margin-top: 3rem;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "Montserrat", sans-serif !important;

  /* Title styling with responsive scaling */
  .title {
    margin-top: 2rem;
    font-size: clamp(
      1.6rem,
      5vw,
      2.5rem
    ); /* Font size dynamically adjusts between 1.6rem and 2.5rem based on screen width */
    font-weight: 700 !important;
    margin-bottom: 1rem;
    color: #0090e7;
  }

  /* Description styling with responsive scaling */
  .description {
    font-size: clamp(
      1.2rem,
      4vw,
      1.5rem
    ); /* Font size dynamically adjusts between 1.2rem and 1.5rem based on screen width */
    color: #6e6e6e;
    margin-bottom: 2rem;
    font-weight: 700 !important;
  }

  /* Card styling with adjusted padding and max-width */
  .expired-card {
    max-width: calc(100% - 40px);
    padding: calc(1.5rem + 1px);
  }

  /* Icon container styling */
  .icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 15px;
  }

  /* Icon styling with scaling on different screens */
  .icon {
    color: #dc3545;
    max-width: 15vw; /* Icon scales dynamically on screen size */
  }

  /* Media Queries for responsive adjustments */

  /* Mobile screens */
  @media (max-width: 576px) {
    .title {
      font-size: clamp(1.4rem, 6vw, 2rem);
    }

    .description {
      font-size: clamp(1.1rem, 5vw, 1.4rem);
    }

    .icon {
      max-width: 25vw;
    }
  }

  /* Tablets */
  @media (max-width: 768px) {
    .title {
      font-size: clamp(1.5rem, 5vw, 2.2rem);
    }

    .description {
      font-size: clamp(1.15rem, 4.5vw, 1.45rem);
    }

    .icon {
      max-width: 20vw;
    }
  }

  /* Laptops & Small desktops */
  @media (max-width: 1024px) {
    .title {
      font-size: clamp(1.6rem, 4.5vw, 2.4rem);
    }

    .description {
      font-size: clamp(1.2rem, 4vw, 1.5rem);
    }

    .icon {
      max-width: 18vw;
    }
  }

  /* Large desktops */
  @media (min-width: 1200px) {
    .title {
      font-size: clamp(1.6rem, 3vw, 2.5rem);
    }

    .description {
      font-size: clamp(1.2rem, 3vw, 1.6rem);
    }

    .icon {
      max-width: 15vw;
    }
  }
}

.dark-layout {
  .expired-link-container {
    .title {
      color: #0090e7;
    }
  }
}

.success-link-container {
  max-width: 90vw;
  margin: 0 auto;
  margin-top: 3rem;
  padding: 1.25rem 0rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: "Montserrat", sans-serif !important;

  .client-program {
    width: 25px !important;
  }
  .mr-05 {
    margin-right: 0.5rem;
  }
  .px-05 {
    padding: 0.5rem;
  }
  .transaction-list {
    display: flex;
    flex-direction: column;
    background-color: #f8f9fa;
    border-radius: 10px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .message-card {
      color: #fff;
      border-radius: 10px;
      margin: 0rem 1rem;
      background-color: #f0f0f0;

      .message-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .transaction-details {
        margin-top: 10px;
        // padding: 10px;
        background: #f0f0f0;
        border-radius: 8px;

        .message-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }
      }

      .text {
        font-size: 0.9rem;
        margin-left: 8px;
        color: #333;
        font-weight: 500;
      }
    }

    /* Animación de aparición/desaparición */
    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 0.3s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
      opacity: 0;
    }
  }
  .text-success {
    color: #28a745;
  }

  .text-danger {
    color: #dc3545;
  }

  /* Title styling with responsive scaling */
  .title {
    margin-top: 2rem;
    font-size: clamp(1.6rem, 5vw, 2.5rem);
    /* Font size dynamically adjusts between 1.6rem and 2.5rem based on screen width */
    font-weight: 700 !important;
    margin-bottom: 1rem;
    color: #0090e7;
  }

  /* Description styling with responsive scaling */
  .description {
    font-size: clamp(1.2rem, 4vw, 1.5rem);
    /* Font size dynamically adjusts between 1.2rem and 1.5rem based on screen width */
    color: #6e6e6e;
    margin-bottom: 2rem;
    font-weight: 700 !important;
  }

  /* Card styling with adjusted padding and max-width */
  .success-card {
    max-width: calc(100% - 40px);
    padding: calc(1.5rem + 1px) 0rem;
  }

  /* Icon container styling */
  .icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 15px;
  }

  /* Icon styling with scaling on different screens */
  .icon {
    color: #dc3545;
    max-width: 15vw;
    /* Icon scales dynamically on screen size */
  }

  /* Media Queries for responsive adjustments */

  /* Mobile screens */
  @media (max-width: 576px) {
    .title {
      font-size: clamp(1.4rem, 6vw, 2rem);
    }

    .description {
      font-size: clamp(1.1rem, 5vw, 1.4rem);
    }

    .icon {
      max-width: 25vw;
    }
  }

  /* Tablets */
  @media (max-width: 768px) {
    .title {
      font-size: clamp(1.5rem, 5vw, 2.2rem);
    }

    .description {
      font-size: clamp(1.15rem, 4.5vw, 1.45rem);
    }

    .icon {
      max-width: 20vw;
    }
  }

  /* Laptops & Small desktops */
  @media (max-width: 1024px) {
    .title {
      font-size: clamp(1.6rem, 4.5vw, 2.4rem);
    }

    .description {
      font-size: clamp(1.2rem, 4vw, 1.5rem);
    }

    .icon {
      max-width: 18vw;
    }
  }

  /* Large desktops */
  @media (min-width: 1200px) {
    .title {
      font-size: clamp(1.6rem, 3vw, 2.5rem);
    }

    .description {
      font-size: clamp(1.2rem, 3vw, 1.6rem);
    }

    .icon {
      max-width: 15vw;
    }
  }
}

.dark-layout {
  .success-link-container {
    .title {
      color: #0090e7;
    }

    .transaction-list {
      background-color: #17171a;

      .message-card {
        background-color: #333;
        color: #fff;
      }

      .transaction-details {
        background: #333;

        .message-row {
          color: #ccc;
        }
      }
      .text {
        color: #fff;
      }
    }

    .label {
      color: #ccc;
    }

    .value {
      color: #fff;
    }
  }
}
</style>
